import { Component, EventEmitter, Input, OnInit, Output, ViewChildren, QueryList, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { JobService } from '../../job.service';
import { JobValueDataForYear } from '../..';
import { BandsService } from '@intranet/modules/admin';
import { BandGroup } from '@intranet/modules/admin/bands-types';
import { Client } from '@intranet/modules/clients/client.types';
import { HotkeyService } from 'src/app/core/services/hotkey.service';
import * as moment from 'moment';
import { Job } from '@intranet/modules/jobs';
import { JobValue } from '@intranet/modules/jobs/job.types';
import { QuillEditorComponent } from 'ngx-quill';
import { take } from 'rxjs/operators';

@Component({
    selector: 'job-value',
    templateUrl: './job-value.component.html',
    styleUrls: ['./job-value.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class JobValueComponent implements OnInit {

    constructor(private jobService: JobService,
                private bandsService: BandsService,
                private hotkeyService: HotkeyService,
                private cdr: ChangeDetectorRef
    ) {}
    public moment = moment;

    @Input() jobId: number;
    @Output() changed = new EventEmitter();
    @Output() cancelled = new EventEmitter();
    @Output() linkedJobsUpdated = new EventEmitter<JobValue>();
    @ViewChildren('quillEditor') editorComponents!: QueryList<QuillEditorComponent>;
    jobValues: JobValueDataForYear[] = [];
    showUserHours: { [key: string]: boolean } = {};
    bandNames: string[] = [];
    bandGroups: BandGroup[] = [];

    showAddDialog: boolean = false;
    newStartDate: Date;
    newRates: number[] = [];
    addError: string = '';
    linkError: string = '';
    disableAddButton: boolean = false;
    showLinkJobDialog: boolean = false;
    clients: Client[] = [];
    currentClientId: number;
    editing: boolean = false;
    newNote: string = '';
    editingJobId: number | null = null;
    noteDrafts: { [jobId: number]: string } = {};
    savingNote = false;
    saveNoteError: string;
    currentEditorJobId: number | null = null;

    ngOnInit(): void {
        // Register a single hotkey for Ctrl+Q to close the correct modal
        this.hotkeyService.registerHotkey('ctrl+q', 'Close modal', () => this.cancel());

        this.getYearData();
        this.getPriceBands();

        // Retrieve the job details to get clientId
        this.jobService.getJobDetails(this.jobId).subscribe({
            next: (jobDetails) => {
                this.currentClientId = jobDetails.job.client.clientId;
            },
            error: (err) => {
                console.error("❌ Failed to retrieve job details:", err);
            }
          });
    }

    private getPriceBands() {
        this.bandsService.getJobPricebands(this.jobId).subscribe({
            next: (r) => {
                if (r.success) {
                    this.bandNames = r.bandNames;
                    this.bandGroups = r.bandGroups;
                }
            }
        });
    }

    private getYearData() {
        this.jobService.getYearJobValues(this.jobId).subscribe({
            next: (r) => {
                this.jobValues = r;
                this.cdr.detectChanges(); // Trigger change detection to update the view
            }
        });
    }

    closeActiveModal() {
        if (this.showAddDialog) {
            this.showAddDialog = false;
        } else {
            this.cancel();
        }
    }

    getHours(values: JobValueDataForYear): number[] {
        let hours: number[] = [];
        hours.push(values.previous.hours);
        values.months.forEach(m => {
            hours.push(m.hours);
        });
        hours.push(values.total.hours);
        return hours;
    }

    getUserHours(values: JobValueDataForYear, user: string): number[] {
        let hours: number[] = [];
        hours.push(values.previous.userHours.find(h => h.user === user).hours);
        values.months.forEach(m => {
            hours.push(m.userHours.find(h => h.user === user).hours);
        });
        hours.push(values.total.userHours.find(h => h.user === user).hours);
        return hours;
    }

    getValues(valuedata: JobValueDataForYear): number[] {
        let values: number[] = [];
        values.push(valuedata.previous.value);
        valuedata.months.forEach(m => {
            values.push(m.value);
        });
        values.push(valuedata.total.value);
        return values;
    }

    getMargins(values: JobValueDataForYear): number[] {
        let margins: number[] = [];
        margins.push(values.previous.margin);
        values.months.forEach(m => {
            margins.push(m.margin);
        });
        margins.push(values.total.margin);
        return margins;
    }

    getNetMargins(values: JobValueDataForYear): number[] {
        let margins: number[] = [];
        margins.push(values.previous.margin - values.previous.value);
        values.months.forEach(m => {
            margins.push(m.margin - m.value);
        });
        margins.push(values.total.margin - values.total.value);
        return margins;
    }

    getMarginsPerHour(values: JobValueDataForYear): number[] {
        let margins: number[] = [];
        if (values.previous.hours > 0)
        {
            margins.push(values.previous.margin / values.previous.hours);
        } else {
            margins.push(0);
        }
        values.months.forEach(m => {
            if (m.hours > 0)
            {
                margins.push(m.margin / m.hours);
            } else {
                margins.push(0);
            }
        });
        if (values.total.hours > 0)
        {
            margins.push(values.total.margin / values.total.hours);
        } else {
            margins.push(0);
        }
        return margins;
    }

    cancel() {
        this.cancelled.emit();
    }

    toggleUserHours(jobTitle: string) {
        this.showUserHours[jobTitle] = !this.showUserHours[jobTitle];
    }

    setNewParent(jobId: number) {
      if (!jobId) return;

      this.jobService.makeLinkedJobParent(jobId).subscribe({
        next: () => {
          this.getYearData(); // optional visual refresh inside modal

          // Emit to refresh the table outside the modal
          this.jobService.getJobDetails(this.jobId).subscribe({
            next: (detail) => {
              this.linkedJobsUpdated.emit(detail.linkedJobsValue); // triggers table update
            },
            error: (err) => {
              console.error('Failed to fetch updated job details after changing parent:', err);
            }
          });
        },
        error: (err) => {
          console.error('Failed to change parent job:', err);
        }
      });
    }

    openAddDialog() {
        this.newStartDate = new Date();
        // initialise newRates to the most recent band rates
        if (this.bandGroups.length > 0) {
            this.newRates = this.bandGroups[this.bandGroups.length - 1].bands.map(b => b.bandRate);
        } else {
            this.newRates = this.bandNames.map(b => 0);
        }

        this.addError = '';
        this.showAddDialog = true;
        this.disableAddButton = false;
    }

    onDownloadOverview() {
        window.open(`/job/downloads/jobtimevaluetotals/${this.jobId}`, '_blank');
    }

    onDownloadDetail() {
        window.open(`/job/downloads/jobtimevaluedetails/${this.jobId}`, '_blank');
    }

    cancelAddDialog() {
        this.showAddDialog = false;
    }

    openLinkJobDialog() {
        this.showLinkJobDialog = true;
    }

    onLinkJob(job: Job) {
        if (!this.jobValues.length) {
            console.error('No parent job available to link to.');
            return;
        }

        const parentJob = this.jobValues.find(j => j.isParent);

        if (!parentJob) {
            console.error('Parent job not found.');
            return;
        }

        this.jobService.linkJobs(parentJob.jobId, job.jobId).subscribe({
          next: (result) => {
            if (!result.success) {
                console.error('Failed to link job:', result.error);
                this.linkError = `Failed to link job: ${result.error}`;
                return;
            }
            this.getYearData(); // Refresh job values list

            // Update linkedJobsValue immediately
            this.jobService.getJobDetails(this.jobId).subscribe({
              next: (detail) => {
                  this.linkedJobsUpdated.emit(detail.linkedJobsValue);
              },
              error: (err) => {
                  console.error('Failed to update linked job values:', err);
              }
          });
        },
            error: (err) => {
                console.error('Failed to link job:', err);
            }
        });
    }

    unlinkJob(jobId: number) {
        if (!jobId) {
            console.error("Invalid jobId for unlinking.");
            return;
        }

        this.jobService.unlinkJob(jobId).subscribe({
            next: () => {
                this.getYearData(); // Refresh job values list

                // Fetch updated linked jobs value and emit event
                this.jobService.getJobDetails(this.jobId).subscribe({
                    next: (detail) => {
                        // If there are no more linked jobs, set linkedJobsValue to null
                        this.linkedJobsUpdated.emit(detail.linkedJobsValue || null);
                    },
                    error: (err) => {
                        console.error('Failed to update linked job values after unlinking:', err);
                    }
                });
            },
            error: (err) => {
                console.error("Failed to unlink job:", err);
            }
        });
    }

    closeLinkJobDialog() {
        this.showLinkJobDialog = false;
    }

    startEdit(jobId: number, existingNote: string = '') {
        this.editingJobId = jobId;
        this.currentEditorJobId = jobId;
        this.noteDrafts[jobId] = existingNote;
    }

    // This triggers when Quill finishes creating the editor
    onEditorCreated(quill: any) {
        // Only focus if we are editing (safety check)
        if (this.currentEditorJobId !== null) {
            const len = quill.getLength();
            quill.focus();
            quill.setSelection(len, 0);
            this.currentEditorJobId = null; // reset
        }
    }

    saveNoteFor(jobId: number) {
        const text = this.noteDrafts[jobId]?.trim();
        if (!text) { return; }
        this.savingNote = true;
        this.saveNoteError = null;

        this.jobService.updateJobNotes(jobId, text).subscribe({
            next: () => {
                const job = this.jobValues.find(j => j.jobId === jobId);
                if (job) { job.notes = text; }
                // force change detection on array
                this.jobValues = [...this.jobValues];
                this.editingJobId = null;
                delete this.noteDrafts[jobId];
            },
            error: () => this.saveNoteError = 'Failed to save note.',
            complete: () => this.savingNote = false
        });
    }

    cancelEdit(jobId: number) {
        if (this.editingJobId === jobId) {
            this.editingJobId = null;
            delete this.noteDrafts[jobId];
        }
    }

    deleteNoteFor(jobId: number) {
        this.savingNote = true;
        this.saveNoteError = null;

        this.jobService.updateJobNotes(jobId, '').subscribe({
            next: () => {
                const job = this.jobValues.find(j => j.jobId === jobId);
                if (job) { job.notes = ''; }
                this.jobValues = [...this.jobValues];
            },
            error: () => this.saveNoteError = 'Failed to delete note.',
            complete: () => this.savingNote = false
        });
    }

    addBands() {
        this.disableAddButton = true;
        let bands = this.bandNames.map((name, i) => {
            return { bandName: name, bandRate: this.newRates[i] };
        });

        this.bandsService.addJobPriceBands(this.jobId, this.newStartDate, bands).subscribe({
            next: (r) => {
                if (r.success) {
                    this.getPriceBands();
                    this.getYearData();
                    this.changed.emit();
                    this.showAddDialog = false;
                } else {
                    this.addError = r.error;
                    this.disableAddButton = false;
                }
            },
            error: (e) => {
                this.addError = e.error.error;
                this.disableAddButton = false;
            }
        });
    }

    deleteBandGroup(group: BandGroup)
    {
        this.bandsService.deleteJobPriceBands(this.jobId, group.startDate).subscribe({
            next: (r) => {
                if (r.success) {
                    this.getPriceBands();
                    this.getYearData();
                    this.changed.emit();
                }
            },
            error: (e) => {
            }
        })
    };
}